import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { debounce, map } from 'lodash'
import type { SearchPageProps } from 'pages/search'

import { Catalog, Meta } from '~/components'
import { catalogSearchQuery } from '~/gql/queries'
import { MainLayout } from '~/layouts'
import { notifySentry, queryUpdater } from '~/utils'

import Header from './header'

const mapper = (value: string, key: string) => `${key}:${value}`

const mapFiltersForQuery = filters => map(filters, mapper)

const SearchScreen = (props: SearchPageProps) => {
  const { metaInfo, isParentCategory, term = '', page, initialFilters, subcategoryName, categoryName } = props

  const filters = mapFiltersForQuery(initialFilters)

  const queryVariables = React.useMemo(
    () => ({
      term,
      page,
      filters,
    }),
    [filters, term, page],
  )

  const catalogSearchQueryResponse = useQuery(catalogSearchQuery, {
    variables: queryVariables,
    onError: apolloError => notifySentry(apolloError),
  })

  const paginationInfo = catalogSearchQueryResponse.data?.catalogSearch?.pageInfo ?? {}

  const products = catalogSearchQueryResponse.data?.catalogSearch?.items ?? []

  const headerTitle = subcategoryName || categoryName || `"${term}"`

  const fetchMoreProducts = React.useCallback(
    async (page: number) => {
      await catalogSearchQueryResponse.fetchMore({
        updateQuery: queryUpdater,
        variables: {
          ...queryVariables,
          page,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryVariables],
  )

  return (
    <>
      <Meta
        title={metaInfo.title}
        image={metaInfo.image}
        url={metaInfo.url}
        description={metaInfo.description}
        asCanonical={isParentCategory}
        prevPage={paginationInfo.prevPage}
        nextPage={paginationInfo.nextPage}
      />
      <MainLayout loading={catalogSearchQueryResponse.loading}>
        <Header title={headerTitle} numOfHints={paginationInfo.total} />
        <Catalog
          products={products}
          pagination={{
            pageStart: paginationInfo.page ?? 1, // need to specify correct page from pagination info when the user has navigated to another page and then goes back and continue scrolling to avoid loading duplicated products
            hasMore: paginationInfo.hasNextPage,
            initialLoad: true,
            loadMore: debounce(fetchMoreProducts, 500), // When the debounce is removed, the package requests page 2 and 3 when it should only request page 2
          }}
        />
      </MainLayout>
    </>
  )
}

export default SearchScreen
